import { PropsWithChildren } from "react";
import { Box, Button, CircularProgress, Grid, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiResponse } from "common/models/ApiResponse";
import {
  FieldValues,
  Path,
  UseFormHandleSubmit,
  UseFormSetError,
} from "react-hook-form";
import { showError, showSuccess } from "utils/showMessages";
import { showServerErrors } from "utils/formUtils";

type FormContainerProps<TKey, TModel, TResponse> = {
  isSubmitting: boolean;
  id?: TKey;
  dataKey: string;
  editMode: boolean;
  setError: UseFormSetError<TModel & FieldValues>;
  handleSubmit: UseFormHandleSubmit<TModel & FieldValues>;
  mutation: UseMutationOptions<ApiResponse<TResponse>, Error, TModel, unknown>;
  breakPoint: number;
};

export function FormContainer<TKey, TModel, TResponse>({
  isSubmitting,
  id,
  dataKey,
  editMode,
  setError,
  handleSubmit,
  mutation,
  breakPoint,
  children,
}: PropsWithChildren<FormContainerProps<TKey, TModel, TResponse>>) {
  const navigateTo = useNavigate();
  const { mutate } = useMutation(mutation);
  const queryClient = useQueryClient();

  const handleResponse = async (result: ApiResponse<TResponse> | undefined) => {
    if (result?.status == 200) {
      if (editMode) {
        queryClient.invalidateQueries({
          queryKey: [dataKey, id],
        });
      }
      showSuccess(`Record ${editMode ? "updated" : "added"} successfully`);
      navigateTo(`/admin/${dataKey}`);
    } else {
      if (result?.status == 404) {
        // console.log("handle 404 here");
      } else if (result?.status == 400) {
        if (result?.modelErrors) {
          const errors = result?.errors as Record<
            Path<TModel & FieldValues>,
            string[]
          >;
          showServerErrors(setError, errors);
          showError(result?.title ?? "");
        } else {
          showError(result?.title ?? "");
        }
      } else {
        showError(result?.title ?? "");
      }
    }
  };

  const onSubmit = handleSubmit((data) => {
    if (isSubmitting) return;
    mutate(data, {
      onSettled(response) {
        handleResponse(response);
      },
    });
  });

  return (
    <>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Box component="form" noValidate onSubmit={onSubmit}>
          {children}
          <Grid
            container
            columnSpacing={2}
            alignItems={"center"}
            justifyContent={"center"}
            sx={{ mt: 2 }}
          >
            <Grid item sm={breakPoint === 0 ? 12 : breakPoint}>
              <Button
                title={editMode ? "Update" : "Save"}
                type="submit"
                disabled={isSubmitting}
                variant="contained"
              >
                {isSubmitting && (
                  <CircularProgress
                    color="inherit"
                    size="1rem"
                    sx={{ mr: 1 }}
                  />
                )}
                {editMode ? "Update" : "Save"}
              </Button>
              <Button
                title="Back"
                sx={{ ml: 1 }}
                variant="contained"
                color="secondary"
                onClick={() => navigateTo(`/admin/${dataKey}`)}
                disabled={isSubmitting}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </>
  );
}
